// Plan 23a Task 3.2 — Modal/Dialog. Drives gsapRegistry directly (not Plan 20a modal-menu-swirl)
// because the hook hardcodes scale 0.85 and Modal needs two coordinated timelines (overlay opacity + panel scale).

import type { UIComponentHandle, UIComponentConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type AccentType = 'error' | 'confirm' | 'success' | 'info';

export interface ModalOpts {
  /** Visual accent type — controls stripe color and semantic intent. */
  accentType: AccentType;
  /** Modal title, rendered as an h3 headline. */
  title: string;
  /** Modal body message text. */
  message: string;
  /** Optional icon element rendered above the title. */
  icon?: HTMLElement;
  /** Pre-built Button elements from Task 2.1 — appended in order. */
  buttons: HTMLElement[];
}

export interface ModalHandle extends UIComponentHandle {
  show(): void;
  hide(): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Animation constants — all values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** animation.duration.normal = 300ms */
const SHOW_DURATION_S = 0.3;

/** animation.duration.fast = 150ms */
const HIDE_DURATION_S = 0.15;

/** animation.easing.bouncy from tokens.json */
const BOUNCY_EASE = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

/** animation.easing.smooth from tokens.json */
const SMOOTH_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

// ─────────────────────────────────────────────────────────────────────────────
// Accent stripe color map — values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** Maps accentType → CSS color value from tokens.json */
const ACCENT_COLOR: Record<AccentType, string> = {
  error: '#D94040', // color.button.danger.base
  confirm: '#E8A020', // color.brand.amber
  success: '#5CAD40', // color.button.success.base
  info: '#4A8ED9', // color.button.secondary.base
};

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createModal(config: UIComponentConfig & ModalOpts): ModalHandle {
  const { gsapRegistry, mount, accentType, title, message, icon, buttons } = config;

  let disposed = false;

  // Timeline handle trackers — four slots (show overlay, show panel, hide overlay, hide panel)
  let showOverlayHandle: GsapTimelineHandle | null = null;
  let showPanelHandle: GsapTimelineHandle | null = null;
  let hideOverlayHandle: GsapTimelineHandle | null = null;
  let hidePanelHandle: GsapTimelineHandle | null = null;

  // ── Build DOM ──────────────────────────────────────────────────────────────

  // Outer wrapper — role=dialog, aria-modal, z-index.modal (300)
  const outer = document.createElement('div');
  outer.setAttribute('role', 'dialog');
  outer.setAttribute('aria-modal', 'true');
  outer.style.cssText = [
    `position: fixed`,
    `inset: 0`,
    `z-index: 300`, // z-index.modal
    `display: flex`,
    `align-items: center`,
    `justify-content: center`,
    `pointer-events: auto`,
  ].join('; ');

  // Overlay — full viewport, dim background
  const overlay = document.createElement('div');
  overlay.setAttribute('data-role', 'modal-overlay');
  overlay.style.cssText = [
    `position: fixed`,
    `inset: 0`,
    `background: rgba(0, 0, 0, 0.60)`, // color.ui.overlay-dim
    `z-index: 0`,
  ].join('; ');
  overlay.style.opacity = '0'; // start invisible

  // Panel wrapper — centered, Panel-styled DOM (inspired by panel.ts styling)
  const panel = document.createElement('div');
  panel.setAttribute('data-role', 'modal-panel');
  panel.style.cssText = [
    `position: relative`,
    `z-index: 1`,
    `background: var(--color-ui-panel-bg-alpha, rgba(30, 35, 50, 0.82))`, // color.ui.panel-bg-alpha
    `border: var(--border-panel, 2px solid rgba(255, 255, 255, 0.10))`,
    `border-radius: var(--border-radius-xl, 16px)`, // border-radius.xl — more rounded per §8
    `box-shadow: var(--shadow-modal, 0 8px 32px rgba(0, 0, 0, 0.50))`, // shadow.modal
    `padding: 32px`, // spacing.xl
    `max-width: 480px`, // layout.modal-max-width
    `width: 100%`,
    `box-sizing: border-box`,
    `overflow: hidden`, // ensures accent stripe clips to border-radius
    `display: flex`,
    `flex-direction: column`,
    `gap: 16px`, // spacing.md between sections
  ].join('; ');
  panel.style.opacity = '0'; // start invisible

  // Accent stripe — 4px across top, flush with border-radius (overflow:hidden on panel clips it)
  const stripe = document.createElement('div');
  stripe.setAttribute('data-role', 'modal-accent-stripe');
  stripe.setAttribute('data-accent', accentType);
  stripe.style.cssText = [
    `position: absolute`,
    `top: 0`,
    `left: 0`,
    `right: 0`,
    `height: 4px`,
    `background: ${ACCENT_COLOR[accentType]}`,
    `border-radius: var(--border-radius-xl, 16px) var(--border-radius-xl, 16px) 0 0`,
  ].join('; ');

  // Content area — rendered below the absolute stripe
  const contentArea = document.createElement('div');
  contentArea.style.cssText = [
    `display: flex`,
    `flex-direction: column`,
    `gap: 12px`, // spacing.sm+ between icon/title/message
    `margin-top: 4px`, // give space for the 4px stripe
  ].join('; ');

  // Optional icon
  if (icon) {
    icon.style.width = '48px';
    icon.style.height = '48px';
    contentArea.appendChild(icon);
  }

  // Title — headline h3, white
  const titleEl = document.createElement('h3');
  titleEl.setAttribute('data-role', 'modal-title');
  titleEl.textContent = title;
  titleEl.style.cssText = [
    `margin: 0`,
    `font-family: 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif`,
    `font-size: 28px`, // typography.size.h3
    `font-weight: 700`,
    `color: #FFFFFF`, // color.text.primary
    `letter-spacing: 1px`,
  ].join('; ');
  contentArea.appendChild(titleEl);

  // Message — body text, secondary color
  const msgEl = document.createElement('p');
  msgEl.setAttribute('data-role', 'modal-message');
  msgEl.textContent = message;
  msgEl.style.cssText = [
    `margin: 0`,
    `font-family: 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif`,
    `font-size: 16px`, // typography.size.body
    `color: #B0B8C8`, // color.text.secondary
    `line-height: 1.5`,
  ].join('; ');
  contentArea.appendChild(msgEl);

  // Buttons container
  const buttonsContainer = document.createElement('div');
  buttonsContainer.setAttribute('data-role', 'modal-buttons');
  buttonsContainer.style.cssText = [
    `display: flex`,
    `gap: 12px`,
    `justify-content: center`,
    `margin-top: 8px`,
  ].join('; ');
  for (const btn of buttons) {
    buttonsContainer.appendChild(btn);
  }

  // Assemble panel
  panel.appendChild(stripe);
  panel.appendChild(contentArea);
  panel.appendChild(buttonsContainer);

  // Assemble outer
  outer.appendChild(overlay);
  outer.appendChild(panel);

  // ── Helpers ────────────────────────────────────────────────────────────────

  function removeFromParent(): void {
    if (outer.parentNode) {
      outer.parentNode.removeChild(outer);
    }
  }

  function killShowTimelines(): void {
    if (showOverlayHandle) {
      showOverlayHandle.dispose();
      showOverlayHandle = null;
    }
    if (showPanelHandle) {
      showPanelHandle.dispose();
      showPanelHandle = null;
    }
  }

  function killHideTimelines(): void {
    if (hideOverlayHandle) {
      hideOverlayHandle.dispose();
      hideOverlayHandle = null;
    }
    if (hidePanelHandle) {
      hidePanelHandle.dispose();
      hidePanelHandle = null;
    }
  }

  function killAllTimelines(): void {
    killShowTimelines();
    killHideTimelines();
  }

  // ── show ──────────────────────────────────────────────────────────────────

  function show(): void {
    if (disposed) return;

    killAllTimelines();

    // Append to mount target if provided and not already mounted
    if (mount && !mount.contains(outer)) {
      mount.appendChild(outer);
    }

    // Overlay timeline: opacity 0 → 1 over SHOW_DURATION_S
    const overlayHandle = gsapRegistry.create('modal:show:overlay');
    overlayHandle.timeline.fromTo(
      overlay,
      { opacity: 0 },
      { opacity: 1, duration: SHOW_DURATION_S },
      0, // explicit position arg — sentinel-safe (F20a-CLASS-1)
    );
    showOverlayHandle = overlayHandle;

    // Panel timeline: scale 0.88 + opacity 0 → scale 1 + opacity 1 over SHOW_DURATION_S, bouncy
    const panelHandle = gsapRegistry.create('modal:show:panel');
    panelHandle.timeline.fromTo(
      panel,
      { scale: 0.88, opacity: 0 },
      { scale: 1, opacity: 1, duration: SHOW_DURATION_S, ease: BOUNCY_EASE },
      0, // explicit position arg — sentinel-safe (F20a-CLASS-1)
    );
    showPanelHandle = panelHandle;
  }

  // ── hide ──────────────────────────────────────────────────────────────────

  function hide(): void {
    if (disposed) return;
    if (!outer.parentNode) return; // not mounted, nothing to hide

    killAllTimelines();

    // Panel timeline: scale 1→0.95 + opacity 1→0 over HIDE_DURATION_S smooth
    const panelHandle = gsapRegistry.create('modal:hide:panel');
    panelHandle.timeline.to(
      panel,
      { scale: 0.95, opacity: 0, duration: HIDE_DURATION_S, ease: SMOOTH_EASE },
      0, // explicit position arg — sentinel-safe (F20a-CLASS-1)
    );
    hidePanelHandle = panelHandle;

    // Overlay timeline: opacity 1→0 over HIDE_DURATION_S, then detach outer
    const overlayHandle = gsapRegistry.create('modal:hide:overlay');
    overlayHandle.timeline.to(
      overlay,
      { opacity: 0, duration: HIDE_DURATION_S },
      0, // explicit position arg — sentinel-safe (F20a-CLASS-1)
    );
    // tl.call(fn, undefined, absoluteSeconds) — NOT eventCallback('onComplete').
    // eventCallback fires at VIRTUAL_DURATION (3600s) due to the sentinel tween.
    overlayHandle.timeline.call(removeFromParent, undefined, HIDE_DURATION_S);
    hideOverlayHandle = overlayHandle;
  }

  // ── dispose ───────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    killAllTimelines();
    removeFromParent();
  }

  return { element: outer, show, hide, dispose };
}
